import { useEffect, useRef } from "react";
import type { Account, MessageEvent as ChatMessage, PublishOutcome } from "@veto/domain";
import { filterExpired } from "@veto/domain";
import { delMeta, getMeta, purgeExpiredMessages, saveDraft } from "@veto/storage";
import type { DirectPeerLink, RelayPool } from "@veto/transport";
import { startOutboxLoop } from "../messaging/wire";
import type { SessionBundle } from "../messaging/accountLifecycle";
import {
  purgeExpiredWakeTokens,
  registerOutboxBackgroundSync,
  resolveWakeToken,
} from "../notifications";

export function useDisappearSweep(opts: {
  activeId: string | null;
  setMessages: (m: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  /** Called (rarely) after the durable sweep deletes rows — refresh list previews. */
  onPurged?: () => void;
}) {
  const onPurgedRef = useRef(opts.onPurged);
  onPurgedRef.current = opts.onPurged;
  useEffect(() => {
    if (!opts.activeId) return;
    const durable = () => {
      void purgeExpiredMessages()
        .then((r) => {
          if (r.purged > 0) onPurgedRef.current?.();
        })
        .catch(() => {
          /* view-layer filter already applied; retry next tick */
        });
    };
    // Durable purge on chat open (cheap indexed query) + ~60s thereafter.
    // The 5s in-memory filter keeps the open chat honest between sweeps.
    let ticks = 0;
    durable();
    const id = window.setInterval(() => {
      opts.setMessages((p) => filterExpired(p));
      ticks += 1;
      if (ticks % 12 === 0) durable();
    }, 5000);
    return () => window.clearInterval(id);
  }, [opts.activeId, opts.setMessages]);
}

export function useDraftAutosave(opts: {
  screen: string;
  activeId: string | null;
  draft: string;
  replyTo: ChatMessage | null;
}) {
  useEffect(() => {
    if (!opts.activeId || opts.screen !== "app") return;
    const t = window.setTimeout(() => {
      void saveDraft({
        conversationId: opts.activeId!,
        text: opts.draft,
        replyToEventId: opts.replyTo?.eventId,
        updatedAt: Date.now(),
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [opts.draft, opts.replyTo, opts.activeId, opts.screen]);
}

export function useSessionRotateOnRevoke(opts: {
  screen: string;
  session: SessionBundle | null;
  showSettings: boolean;
}) {
  useEffect(() => {
    if (opts.screen !== "app" || !opts.session) return;
    void getMeta("devices.rotateSessionsAt").then(async (at) => {
      if (!at) return;
      // Legacy plaintext handshake cache (pre sealed-IDB) — scrub if present.
      for (const k of Object.keys(sessionStorage).filter((x) => x.startsWith("veto.hs."))) {
        sessionStorage.removeItem(k);
      }
      // FR-DEV-003: retire every session (each next send runs a fresh hybrid AKE)
      // under each conversation's lock, keeping the replay caches so old
      // first-contact messages can never re-open a session (review H2/L4).
      try {
        await opts.session!.store.retireAll();
      } catch {
        /* retry next trigger */
        return;
      }
      // One-shot flag: consume so rotation does not re-fire every render.
      try {
        await delMeta("devices.rotateSessionsAt");
      } catch {
        /* re-fires next toggle; harmless */
      }
    });
  }, [opts.screen, opts.session, opts.showSettings]);
}

/**
 * Durable wire-outbox retry (FR-MSG-001, SPEC §12): republishes stored sealed
 * events verbatim — covers direct texts, group copies, edits and reactions.
 */
export function useWireOutbox(opts: {
  screen: string;
  account: Account | null;
  relay: RelayPool;
  peers: Map<string, DirectPeerLink>;
  onMessageStatus: (u: { messageEventId: string; conversationId: string } & PublishOutcome) => void;
}) {
  const latest = useRef(opts);
  latest.current = opts;
  useEffect(() => {
    if (opts.screen !== "app") return;
    const stop = startOutboxLoop(
      () => {
        const o = latest.current;
        return o.account ? { account: o.account, relay: o.relay, peers: o.peers } : null;
      },
      (u) => latest.current.onMessageStatus(u),
    );
    void registerOutboxBackgroundSync();
    const onSwMessage = (event: Event) => {
      const data = (event as { data?: { type?: string } }).data;
      if (data?.type === "veto:outbox-sync") void registerOutboxBackgroundSync();
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => {
      stop();
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
  }, [opts.screen]);
}

export function useNotificationDeepLinks(opts: {
  screen: string;
  openConversation: (id: string) => Promise<void>;
}) {
  useEffect(() => {
    if (opts.screen !== "app") return;
    purgeExpiredWakeTokens();

    const openConversationId = (id: string | null | undefined) => {
      if (id) void opts.openConversation(id);
    };

    const openFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const wake = params.get("wake");
      const open = params.get("open");
      if (wake) {
        openConversationId(resolveWakeToken(wake));
        window.history.replaceState({}, "", "/");
        return;
      }
      if (open) {
        openConversationId(open);
        window.history.replaceState({}, "", "/");
      }
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<{ conversationId?: string; token?: string }>).detail;
      if (detail?.conversationId) openConversationId(detail.conversationId);
      else if (detail?.token) openConversationId(resolveWakeToken(detail.token));
    };
    const onSwMessage = (event: Event) => {
      const data = (event as { data?: { type?: string; conversationId?: string; token?: string } })
        .data;
      if (data?.type !== "veto:open-conversation") return;
      if (data.conversationId) openConversationId(data.conversationId);
      else if (data.token) openConversationId(resolveWakeToken(data.token));
    };
    openFromUrl();
    window.addEventListener("veto:open-conversation", onCustom);
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => {
      window.removeEventListener("veto:open-conversation", onCustom);
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.screen]);
}
